import type { DocumentPickerAsset } from "expo-document-picker";

import type { Deliverable, DeliverableFileType } from "../types/database";
import { logAdminAction } from "./auditLog";
import { readUploadBody } from "./submissions";
import { supabase } from "./supabase";

export async function listDeliverables(competitionId: string): Promise<Deliverable[]> {
  const { data, error } = await supabase
    .from("deliverables")
    .select("*")
    .eq("competition_id", competitionId)
    .order("sequence", { ascending: true });

  if (error) {
    console.warn("[deliverables] list failed:", error.message);
    return [];
  }
  if (!data) return [];
  return data as Deliverable[];
}

/**
 * Server-redacted deliverables for a specific team. The DB function returns
 * `description` and `template_url` as NULL for any deliverable whose prior
 * locked-gate the team hasn't passed, so even direct API calls can't leak the
 * brief copy of a locked deliverable. Use this on the student competition
 * detail page when the user is on a team; falls back to `listDeliverables`
 * for non-team viewers.
 */
export async function listDeliverablesForTeam(teamId: string): Promise<Deliverable[]> {
  const { data, error } = await supabase.rpc("student_team_deliverables", { p_team_id: teamId });
  if (error || !data) return [];
  return data as Deliverable[];
}

export async function getDeliverable(id: string): Promise<Deliverable | null> {
  const { data, error } = await supabase.from("deliverables").select("*").eq("id", id).maybeSingle();
  if (error || !data) return null;
  return data as Deliverable;
}

async function nextSequence(competitionId: string): Promise<number> {
  const { data } = await supabase
    .from("deliverables")
    .select("sequence")
    .eq("competition_id", competitionId)
    .order("sequence", { ascending: false })
    .limit(1)
    .maybeSingle();
  const n = data?.sequence;
  return typeof n === "number" && !Number.isNaN(n) ? n + 1 : 1;
}

export type CreateDeliverableInput = {
  name: string;
  file_type: DeliverableFileType;
  description?: string | null;
  rubric?: string | null;
  point_value?: number | null;
  is_scored?: boolean;
  is_locked_gate?: boolean;
  allow_revisions?: boolean;
  opens_at?: string | null;
  due_at?: string | null;
  sequence?: number;
};

export async function createDeliverable(
  competitionId: string,
  payload: CreateDeliverableInput
): Promise<{ id: string | null; error: Error | null }> {
  const sequence = payload.sequence ?? (await nextSequence(competitionId));
  const rubricVal = payload.rubric ?? null;

  const { data, error } = await supabase
    .from("deliverables")
    .insert({
      competition_id: competitionId,
      sequence,
      name: payload.name,
      description: payload.description ?? null,
      file_type: payload.file_type,
      rubric: rubricVal as unknown as Deliverable["rubric"],
      point_value: payload.point_value ?? null,
      is_scored: payload.is_scored ?? false,
      is_locked_gate: payload.is_locked_gate ?? false,
      allow_revisions: payload.allow_revisions ?? true,
      opens_at: payload.opens_at ?? null,
      due_at: payload.due_at ?? null,
      template_url: null,
    })
    .select("id")
    .single();

  if (error || !data?.id) return { id: null, error: new Error(error?.message ?? "Insert failed") };
  await logAdminAction({
    action: "deliverable.create",
    entityType: "deliverables",
    entityId: data.id as string,
    summary: `Created deliverable: ${payload.name}`,
    diff: {
      after: {
        competition_id: competitionId,
        name: payload.name,
        file_type: payload.file_type,
        is_locked_gate: payload.is_locked_gate ?? false,
      },
    },
  });
  return { id: data.id as string, error: null };
}

export async function updateDeliverable(
  id: string,
  patch: Partial<
    Omit<Deliverable, "id" | "competition_id"> & { rubric?: string | null | Deliverable["rubric"] }
  >
): Promise<{ error: Error | null }> {
  const { error } = await supabase.from("deliverables").update(patch).eq("id", id);
  if (error) return { error: new Error(error.message) };
  await logAdminAction({
    action: "deliverable.update",
    entityType: "deliverables",
    entityId: id,
    summary: patch.name ? `Updated deliverable: ${patch.name}` : `Updated deliverable`,
    diff: { after: patch as Record<string, unknown> },
  });
  return { error: null };
}

export async function deleteDeliverable(id: string): Promise<{ error: Error | null }> {
  // Snapshot the row name before delete so the audit summary is readable
  // (otherwise we'd just have an opaque id). Best-effort — if the read
  // fails we still try the delete and log with a generic summary.
  const { data: before } = await supabase
    .from("deliverables")
    .select("name, competition_id")
    .eq("id", id)
    .maybeSingle();
  const { error } = await supabase.from("deliverables").delete().eq("id", id);
  if (error) return { error: new Error(error.message) };
  await logAdminAction({
    action: "deliverable.delete",
    entityType: "deliverables",
    entityId: id,
    summary: before?.name ? `Deleted deliverable: ${before.name}` : `Deleted deliverable`,
    diff: { before: before ?? null, after: null },
  });
  return { error: null };
}

export async function reorderDeliverables(
  competitionId: string,
  ids: string[]
): Promise<{ error: Error | null }> {
  for (let i = 0; i < ids.length; i++) {
    const { error } = await supabase
      .from("deliverables")
      .update({ sequence: i + 1 })
      .eq("id", ids[i])
      .eq("competition_id", competitionId);
    if (error) return { error: new Error(error.message) };
  }
  return { error: null };
}

function safeFileName(name: string): string {
  return name.replace(/[^\w.\-()+]/g, "_");
}

/** Uploads to `templates` bucket at `{competitionId}/{deliverableId}/{filename}`. Updates `template_url` with storage path (signed at read time). */
export async function uploadDeliverableTemplate(
  competitionId: string,
  deliverableId: string,
  file: DocumentPickerAsset
): Promise<{ error: Error | null }> {
  const path = `${competitionId}/${deliverableId}/${safeFileName(file.name)}`;
  const { body, contentType } = await readUploadBody(file);

  const { error: upErr } = await supabase.storage.from("templates").upload(path, body, {
    contentType,
    upsert: true,
  });
  if (upErr) return { error: new Error(upErr.message) };

  const { error: dbErr } = await supabase
    .from("deliverables")
    .update({ template_url: path })
    .eq("id", deliverableId)
    .eq("competition_id", competitionId);
  return { error: dbErr ? new Error(dbErr.message) : null };
}

/** Resolve template link for opening: full URLs pass through; storage paths get a signed URL. */
export async function getDeliverableTemplateSignedUrl(
  templateUrl: string | null
): Promise<{ url: string | null; error: Error | null }> {
  if (!templateUrl) return { url: null, error: null };
  if (/^https?:\/\//i.test(templateUrl)) {
    return { url: templateUrl, error: null };
  }
  const { data, error } = await supabase.storage.from("templates").createSignedUrl(templateUrl, 3600);
  if (error) return { url: null, error: new Error(error.message) };
  return { url: data?.signedUrl ?? null, error: null };
}